// ============================================================================
// GumaHUB Authentication Service
// ============================================================================

import { api, setTokens, clearTokens, getTokens } from '@gumahub/shared';
import type { AuthUser, LoginResponse } from '@gumahub/shared';

const AUTH_ENDPOINTS = {
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  REFRESH: '/auth/refresh',
  ME: '/auth/me',
  CHANGE_PASSWORD: '/auth/password',
  COMPANIES: '/auth/companies',
  SWITCH_COMPANY: '/auth/switch-company',
};

class AuthService {
  /**
   * Login with username and password
   */
  async login(username: string, password: string): Promise<{
    success: boolean;
    user?: AuthUser;
    error?: string;
  }> {
    const response = await api.post<LoginResponse>(AUTH_ENDPOINTS.LOGIN, {
      username,
      password,
    });

    if (response.success && response.data) {
      setTokens(response.data.token, response.data.refreshToken);
      return {
        success: true,
        user: response.data.user,
      };
    }

    return {
      success: false,
      error: response.error || 'Login failed',
    };
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await api.post(AUTH_ENDPOINTS.LOGOUT);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearTokens();
    }
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<{
    success: boolean;
    user?: AuthUser;
    error?: string;
  }> {
    const { accessToken } = getTokens();
    if (!accessToken) {
      return { success: false, error: 'No access token' };
    }

    const response = await api.get<AuthUser>(AUTH_ENDPOINTS.ME);

    if (response.success && response.data) {
      return {
        success: true,
        user: response.data,
      };
    }

    return {
      success: false,
      error: response.error || 'Failed to get user info',
    };
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<{
    success: boolean;
    user?: AuthUser;
    error?: string;
  }> {
    const { refreshToken } = getTokens();
    if (!refreshToken) {
      return { success: false, error: 'No refresh token' };
    }

    const response = await api.post<LoginResponse>(AUTH_ENDPOINTS.REFRESH, {
      refreshToken,
    });

    if (response.success && response.data) {
      setTokens(response.data.token, response.data.refreshToken);
      return {
        success: true,
        user: response.data.user,
      };
    }

    clearTokens();
    return {
      success: false,
      error: response.error || 'Token refresh failed',
    };
  }

  /**
   * Change password
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    const response = await api.put<void>(AUTH_ENDPOINTS.CHANGE_PASSWORD, {
      currentPassword,
      newPassword,
    });

    if (response.success) {
      return { success: true };
    }

    return {
      success: false,
      error: response.error || 'Password change failed',
    };
  }

  /**
   * Check if user has valid session
   */
  hasSession(): boolean {
    const { accessToken } = getTokens();
    return !!accessToken;
  }

  /**
   * Switch to a different company
   */
  async switchCompany(clientId: string): Promise<{
    success: boolean;
    user?: AuthUser;
    error?: string;
  }> {
    const response = await api.post<LoginResponse>(
      `${AUTH_ENDPOINTS.SWITCH_COMPANY}/${clientId}`
    );

    if (response.success && response.data) {
      setTokens(response.data.token, response.data.refreshToken);
      return {
        success: true,
        user: response.data.user,
      };
    }

    return {
      success: false,
      error: response.error || 'Failed to switch company',
    };
  }
}

export const authService = new AuthService();
export default authService;
